import React, { useState, useRef, useEffect } from 'react';
import { createStamp, createStampByObject, createStampByCoordinates } from '../../api/images';
import { CodePopup } from '../CodeSnippet';
import { FormInput, FormSelect, FormFileSelect, FormGroup } from './FormComponents';

// Reuse the DashboardCard component style from your Dashboard
const DashboardCard = ({ title, children, className }) => {
  return (
    <div className={`p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md transition-colors ${className}`}>
      <h2 className="mb-4 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">{title}</h2>
      {children}
    </div>
  );
};

const StampTool = ({ collectionId, onStampCreated, availableFiles, prefilledData }) => {
  const [searchMode, setSearchMode] = useState('coordinates');
  const [ra, setRa] = useState('');
  const [dec, setDec] = useState('');
  const [coordinates, setCoordinates] = useState(''); // Combined RA, Dec input
  const [size, setSize] = useState('5.0');
  const [sizeUnit, setSizeUnit] = useState('arcmin');
  const [filter, setFilter] = useState('');
  const [objectName, setObjectName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [format, setFormat] = useState('fits');
  const [zmin, setZmin] = useState('1');
  const [zmax, setZmax] = useState('99');
  const [pattern, setPattern] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stampUrl, setStampUrl] = useState(null);
  const [imageData, setImageData] = useState(null);
  const [fileSearchText, setFileSearchText] = useState('');
  const [filteredFiles, setFilteredFiles] = useState([]);
  const canvasRef = useRef(null);
  const [codePopup, setCodePopup] = useState({ isOpen: false, snippetName: '', dynamicData: {} });

  // Parse coordinates from combined input
  const parseCoordinates = (coordStr) => {
    if (!coordStr) return { ra: '', dec: '' };
    // Support formats: "150.5, -23.4" or "150.5 -23.4"
    const parts = coordStr.split(/[,\s]+/).filter(p => p.trim());
    if (parts.length >= 2) {
      return { ra: parts[0].trim(), dec: parts[1].trim() };
    }
    return { ra: '', dec: '' };
  };

  // Handle combined coordinate input
  const handleCoordinatesChange = (value) => {
    setCoordinates(value);
    const { ra: parsedRa, dec: parsedDec } = parseCoordinates(value);
    setRa(parsedRa);
    setDec(parsedDec);
  };

  // Handle prefilled data from ImageBrowser
  useEffect(() => {
    if (prefilledData) {
      if (prefilledData.ra_center !== undefined && prefilledData.dec_center !== undefined) {
        const raVal = prefilledData.ra_center.toString();
        const decVal = prefilledData.dec_center.toString();
        setRa(raVal);
        setDec(decVal);
        setCoordinates(`${raVal}, ${decVal}`);
      }
      if (prefilledData.filter) {
        setFilter(prefilledData.filter);
      }
      if (prefilledData.object_name) {
        setObjectName(prefilledData.object_name);
      }
      if (prefilledData.file_id && availableFiles) {
        const file = availableFiles.find(f => f.id === prefilledData.file_id);
        if (file) {
          setSelectedFile(file);
          setSearchMode('file');
        }
      } else {
        // Set search mode to coordinates when prefilled
        setSearchMode('coordinates');
      }
    }
  }, [prefilledData, availableFiles]);

  // Update filtered files whenever availableFiles or search text changes
  useEffect(() => {
    if (!availableFiles || !fileSearchText) {
      setFilteredFiles(availableFiles || []);
      return;
    }
    const lowerCaseSearch = fileSearchText.toLowerCase();
    const filtered = availableFiles.filter(file => {
      const filename = file.filename?.toLowerCase() || '';
      const filter = file.filter?.toLowerCase() || '';
      const objectName = file.object_name?.toLowerCase() || '';
      return filename.includes(lowerCaseSearch) || 
             filter.includes(lowerCaseSearch) || 
             objectName.includes(lowerCaseSearch);
    });
    setFilteredFiles(filtered);
  }, [availableFiles, fileSearchText]);

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      let result;
      if (searchMode === 'coordinates') {
        result = await createStampByCoordinates(
          collectionId,
          parseFloat(ra),
          parseFloat(dec),
          parseFloat(size),
          filter,
          format,
          sizeUnit,
          format === 'png' ? parseFloat(zmin) : null,
          format === 'png' ? parseFloat(zmax) : null,
          pattern || undefined
        );
      } else if (searchMode === 'object') {
        result = await createStampByObject(
          collectionId,
          objectName,
          filter,
          parseFloat(size),
          format,
          sizeUnit,
          parseFloat(ra),
          parseFloat(dec),
          format === 'png' ? parseFloat(zmin) : null,
          format === 'png' ? parseFloat(zmax) : null,
          pattern || undefined
        );
      } else if (searchMode === 'file' && selectedFile) {
        result = await createStamp(
          selectedFile.id,
          parseFloat(ra),
          parseFloat(dec),
          parseFloat(size),
          sizeUnit,
          format,
          format === 'png' ? parseFloat(zmin) : null,
          format === 'png' ? parseFloat(zmax) : null
        );
      }
      
      // Create a URL for the returned image blob
      const url = URL.createObjectURL(result.data);
      setStampUrl(url);
      setImageData(result);

      // Execute the optional callback if provided
      if (onStampCreated) {
        onStampCreated(result);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create stamp');
    } finally {
      setLoading(false);
    }
  };

  const handleFormatChange = (e) => {
    setFormat(e.target.value);
    // Clear the previous result when changing the format.
    setStampUrl(null);
    setImageData(null);
  };

  return (
    <DashboardCard title="Create Postage Stamp">
      <div className="mb-4">
        {/* Tab Navigation */}
        <div className="flex border-b border-ui-light-border dark:border-ui-dark-border mb-4">
          <button
            onClick={() => setSearchMode('coordinates')}
            className={`px-4 py-2 -mb-px font-medium text-sm focus:outline-none ${
              searchMode === 'coordinates'
                ? 'border-b-2 border-primary-600 text-ui-light-text-primary dark:text-ui-dark-text-primary'
                : 'text-ui-light-text-secondary dark:text-ui-dark-text-tertiary'
            }`}
          >
            By Coordinates
          </button>
          <button
            onClick={() => setSearchMode('object')}
            className={`px-4 py-2 -mb-px font-medium text-sm focus:outline-none ${
              searchMode === 'object'
                ? 'border-b-2 border-primary-600 text-ui-light-text-primary dark:text-ui-dark-text-primary'
                : 'text-ui-light-text-secondary dark:text-ui-dark-text-tertiary'
            }`}
          >
            By Object Name
          </button>
          <button
            onClick={() => setSearchMode('file')}
            className={`px-4 py-2 -mb-px font-medium text-sm focus:outline-none ${
              searchMode === 'file'
                ? 'border-b-2 border-primary-600 text-ui-light-text-primary dark:text-ui-dark-text-primary'
                : 'text-ui-light-text-secondary dark:text-ui-dark-text-tertiary'
            }`}
          >
            By File Name
          </button>
        </div>

        {/* Form Fields */}
        <form className="space-y-4">
          {searchMode === 'coordinates' && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-2">
                Coordinates (RA, Dec)
              </label>
              <input
                type="text"
                value={coordinates}
                onChange={(e) => handleCoordinatesChange(e.target.value)}
                placeholder="e.g., 150.5, -23.4 or 150.5 -23.4"
                className="mt-1 block w-full px-3 py-2.5 border border-ui-light-border dark:border-ui-dark-border rounded-xl bg-white dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                required
              />
              <p className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted mt-1 ml-1">
                Enter coordinates as: RA (0-360°), Dec (-90 to 90°)
              </p>
            </div>
          )}
          {searchMode === 'object' && (
            <>
              <FormInput
                label="Object Name"
                value={objectName}
                onChange={(e) => setObjectName(e.target.value)}
                placeholder="Enter object name"
                required
              />
              <FormInput
                label="Coordinates (RA, Dec)"
                value={coordinates}
                onChange={(e) => handleCoordinatesChange(e.target.value)}
                placeholder="e.g., 150.5, -23.4 or 150.5 -23.4"
                helpText="Enter coordinates as: RA (0-360°), Dec (-90 to 90°)"
                required
              />
            </>
          )}
          {searchMode === 'file' && (
            <>
              <FormFileSelect
                label="File Name"
                searchValue={fileSearchText}
                onSearchChange={(e) => setFileSearchText(e.target.value)}
                selectedValue={selectedFile ? selectedFile.id : ''}
                onSelectChange={(e) => {
                  const fileId = e.target.value;
                  // Find in filteredFiles and convert ID to match type (string or number)
                  const file = filteredFiles.find(f => f.id == fileId); // Use == for type coercion
                  setSelectedFile(file);
                  if (file) {
                    const raVal = file.ra_center?.toString() || '';
                    const decVal = file.dec_center?.toString() || '';
                    setRa(raVal);
                    setDec(decVal);
                    setCoordinates(raVal && decVal ? `${raVal}, ${decVal}` : '');
                    setFilter(file.filter || '');
                  }
                }}
                files={filteredFiles}
              />
              <FormInput
                label="Coordinates (RA, Dec)"
                value={coordinates}
                onChange={(e) => handleCoordinatesChange(e.target.value)}
                placeholder="e.g., 150.5, -23.4 or 150.5 -23.4"
                helpText="Enter coordinates as: RA (0-360°), Dec (-90 to 90°)"
                required
              />
            </>
          )}

          <FormInput
            label="Filter Name"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={searchMode === 'file' ? "Optional: Override the filter" : "Enter filter name"}
            helpText={searchMode === 'file' ? "Optional: Override the filter from the selected file" : "Required"}
            required={searchMode !== 'file'}
          />

          <FormGroup>
            <FormInput
              label="Size"
              type="number"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              className="w-1/2"
              required
            />
            <FormSelect
              label="Unit"
              value={sizeUnit}
              onChange={(e) => setSizeUnit(e.target.value)}
              options={[
                { value: 'arcmin', label: 'arcmin' },
                { value: 'arcsec', label: 'arcsec' },
                { value: 'pixels', label: 'pixel' }
              ]}
              className="w-1/2"
            />
          </FormGroup>

          <FormInput
            label="Pattern (optional)"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder="Optional filename pattern to match"
            helpText="Filter files by partial filename match"
          />

          <FormSelect
            label="Format"
            value={format}
            onChange={handleFormatChange}
            options={[
              { value: 'fits', label: 'FITS' },
              { value: 'png', label: 'PNG' }
            ]}
          />

          {format === 'png' && (
            <FormGroup>
              <FormInput
                label="Zmin percentile"
                type="number"
                value={zmin}
                onChange={(e) => setZmin(e.target.value)}
                placeholder="0-100"
                helpText="Optional: Enter value between 0 and 100"
                className="w-1/2"
              />
              <FormInput
                label="Zmax percentile"
                type="number"
                value={zmax}
                onChange={(e) => setZmax(e.target.value)}
                placeholder="0-100"
                helpText="Optional: Enter value between 0 and 100"
                className="w-1/2"
              />
            </FormGroup>
          )}

          {error && (
            <div className="p-2 mt-4 text-sm text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
              {error}
            </div>
          )}

          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
            >
              {loading ? (
                <>
                  <svg className="w-5 h-5 inline-block mr-2 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                'Create Stamp'
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                const snippetName = searchMode === 'coordinates' ? 'stamp_by_coordinates' :
                                  searchMode === 'object' ? 'stamp_by_object' : 'stamp_by_file';
                setCodePopup({
                  isOpen: true,
                  snippetName,
                  dynamicData: {
                    base_url: window.location.origin,
                    collection_id: collectionId,
                    ra: ra || '0.0',
                    dec: dec || '0.0',
                    size: size || '1.0',
                    filter: filter || 'R',
                    size_unit: sizeUnit,
                    format: format,
                    pattern: pattern || '',
                    object_name: objectName || '',
                    file_id: selectedFile?.id || ''
                  }
                });
              }}
              className="px-4 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-hover dark:bg-ui-dark-hover rounded-md hover:bg-ui-light-card dark:hover:bg-ui-dark-card focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors flex items-center gap-2"
              title="Show Python code"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Code
            </button>
          </div>
        </form>

        {/* Display the result (stamp) */}
        {stampUrl && (
          <div className="mt-6 flex flex-col items-center gap-4">
            {format === 'png' ? (
              <div className="p-2 bg-ui-light-card dark:bg-ui-dark-card rounded-md shadow-md">
                <img src={stampUrl} alt="Generated stamp" className="max-w-full" />
              </div>
            ) : (
              <a
                href={stampUrl}
                download={imageData?.filename || 'stamp.fits'}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
              >
                Download FITS File
              </a>
            )}
          </div>
        )}

        {/* Display image metadata if available */}
        {imageData?.metadata && (
          <div className="mt-6">
            <h3 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-2">Image Metadata:</h3>
            <pre className="max-h-40 overflow-auto bg-ui-light-card dark:bg-ui-dark-card p-4 rounded-md text-xs text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
              {JSON.stringify(imageData.metadata, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Code Popup */}
      <CodePopup
        isOpen={codePopup.isOpen}
        onClose={() => setCodePopup({ isOpen: false, snippetName: '', dynamicData: {} })}
        snippetName={codePopup.snippetName}
        dynamicData={codePopup.dynamicData}
      />
    </DashboardCard>
  );
};

export default StampTool;